import {
  AccessibleDivButton,
  AccessibleList,
  AccessibleListItem,
  Divider,
  EmptyState,
  FavoriteIcon,
  LiveFeedback,
  LoadingSpinner,
  SortMenu,
} from '@/components';
import { APP_ROUTES, DEFAULT_ERROR_MESSAGE } from '@/config/constants';
import {
  ButtonFlatMediumEmphasized,
  NavBarBackButton,
  Tag,
  TitleSmallEmphasized,
  useSnackbar,
} from '@/design-system';
import { UNSAVE_FLYER_MUTATION } from '@/graphql/mutation';
import { districtVar } from '@/graphql/state';
import {
  AllFlyerFilters,
  FlyerCardThumbnail,
  GetAllFlyersVars,
  UnsaveFlyerResponse,
} from '@/graphql/types';
import {
  useAnalytics,
  useEffectOnMount,
  useFlyerboardPagination,
  useLiveFeedback,
  useSchools,
} from '@/hooks';
import colors from '@/theme/colors';
import { FlyerStatus } from '@/types/flyer';
import { ApolloCache, useApolloClient, useMutation, useReactiveVar } from '@apollo/client';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { useEffect, useMemo, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TrackingEvents, TrackingStage } from '@/config/tracking';
import { Card } from './components/Card';
import { RemoveSavedFlyerModal } from './components/RemoveSavedFlyerModal';
import styles from './Saved.styles';

const DEFAULT_SORT_VALUE = 'flyerId';

const SavedFlyersKeyPattern = /^getAllFlyers:.+"isFavorite":true.+$/;
export function clearSavedFlyersCache(cache: ApolloCache<object>, status?: FlyerStatus) {
  cache.modify({
    fields: {
      getAllFlyers(getAllFlyersRef, { storeFieldName, DELETE }) {
        if (SavedFlyersKeyPattern.test(storeFieldName)) {
          if (!status) {
            return DELETE;
          }
          try {
            const args: GetAllFlyersVars = JSON.parse(storeFieldName.replace('getAllFlyers:', ''));
            if (
              status === FlyerStatus.active &&
              !args.input.filters.isExpired &&
              !args.input.filters.isRemoved
            ) {
              return DELETE;
            }
            if (status === FlyerStatus.expired && args.input.filters.isExpired) {
              return DELETE;
            }
            if (status === FlyerStatus.removed && args.input.filters.isRemoved) {
              return DELETE;
            }
          } catch {
            return getAllFlyersRef;
          }
        }
        return getAllFlyersRef;
      },
    },
  });
}

export const Saved: React.FC = () => {
  const client = useApolloClient();
  const trackId = 'saved';
  const ref = useRef<HTMLDivElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const showToast = useSnackbar();
  const { trackEvent } = useAnalytics();
  const { hasMultipleChildren } = useSchools();
  const [unsaveFlyers] = useMutation<UnsaveFlyerResponse>(UNSAVE_FLYER_MUTATION);
  const [unsaveFlyerId, setUnsaveFlyerId] = useState<number>(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [selectedSortValue, setSelectedSortValue] = useState<string>(
    searchParams.get('sort') || DEFAULT_SORT_VALUE
  );
  const sortOptions = [
    { value: 'flyerId', label: 'Recently posted' },
    { value: 'owningType', label: 'Provider', description: 'School, Community' },
    ...(hasMultipleChildren ? [{ value: 'gradeOrAge', label: 'Grade, Age' }] : []),
    { value: 'category', label: 'Type (A-Z)', description: 'Academics, Arts, Athletics...' },
  ];

  const [liveRegionMessage, setLiveRegionMessage] = useLiveFeedback({ delay: 2000 });
  const nextFocusRef = useRef<HTMLElement | null>(null);

  useEffectOnMount(() => {
    trackEvent({
      event: TrackingEvents.viewSavedFlyers,
      event_detail: TrackingEvents.viewSavedFlyers,
      stage: TrackingStage.consideration,
    });
  });

  useEffect(() => {
    const sortFromUrl = searchParams.get('sort');
    if (sortFromUrl) {
      setSelectedSortValue(sortFromUrl);
    }
  }, [searchParams]);

  const district = useReactiveVar(districtVar);

  const activeFlyersFilters = useMemo<AllFlyerFilters>(() => {
    return {
      allDistrictSchools: true,
      schoolIds: [],
      isFavorite: true,
    };
  }, []);

  const expiredFlyersFilters = useMemo<AllFlyerFilters>(() => {
    return {
      ...activeFlyersFilters,
      isExpired: true,
    };
  }, [activeFlyersFilters]);

  const removedFlyersFilters = useMemo<AllFlyerFilters>(() => {
    return {
      ...activeFlyersFilters,
      isRemoved: true,
    };
  }, [activeFlyersFilters]);

  const {
    flyers: activeFlyers,
    loading: loadingActiveFlyers,
    fetchMoreFlyers: fetchMoreActiveFlyers,
    total: totalActiveFlyers,
    isPaginationEnd: isActivePaginationEnd,
  } = useFlyerboardPagination({
    skip: !district,
    districtId: district?.districtId,
    shouldUpdateFacets: false,
    flyerboardFilters: activeFlyersFilters,
    sortBy: selectedSortValue,
    shouldTrackImpressions: false,
  });

  const {
    flyers: expiredFlyers,
    loading: loadingExpiredFlyers,
    fetchMoreFlyers: fetchMoreExpiredFlyers,
    total: totalExpiredFlyers,
    isPaginationEnd: isExpiredPaginationEnd,
  } = useFlyerboardPagination({
    skip: !isActivePaginationEnd,
    districtId: district?.districtId,
    shouldUpdateFacets: false,
    flyerboardFilters: expiredFlyersFilters,
    sortBy: selectedSortValue,
    shouldTrackImpressions: false,
  });

  const {
    flyers: removedFlyers,
    loading: loadingRemovedFlyers,
    fetchMoreFlyers: fetchMoreRemovedFlyers,
    total: totalRemovedFlyers,
  } = useFlyerboardPagination({
    skip: !isExpiredPaginationEnd,
    districtId: district?.districtId,
    shouldUpdateFacets: false,
    flyerboardFilters: removedFlyersFilters,
    sortBy: selectedSortValue,
    shouldTrackImpressions: false,
  });

  const loading = loadingActiveFlyers || loadingExpiredFlyers || loadingRemovedFlyers;

  const onCancelRemoveSavedFlyer = () => {
    setUnsaveFlyerId(0);
  };

  const onClickUnsaveFlyers = async ({ isExpired }: { isExpired: boolean }) => {
    const flyersList = isExpired ? expiredFlyers : removedFlyers;
    const flyerIds = flyersList.map(flyer => flyer.flyerId);
    nextFocusRef.current = null;
    await onUnSaveFlyers(flyerIds);
  };

  const onUnSaveFlyers = async (flyerIds: number[]) => {
    try {
      const { data } = await unsaveFlyers({
        variables: {
          input: {
            flyerIds,
          },
        },
      });
      if (data?.unsaveFlyers.success) {
        let removedActiveCount = 0;
        let removedExpiredCount = 0;
        let removedRemovedCount = 0;

        flyerIds.forEach(flyerId => {
          client.cache.modify({
            id: `Flyer:{"flyerId":${flyerId}}`,
            fields: {
              isFavorite: () => false,
            },
          });

          const isActiveFlyer = activeFlyers.some(flyer => flyer.flyerId === flyerId);
          const isExpiredFlyer =
            !isActiveFlyer && expiredFlyers.some(flyer => flyer.flyerId === flyerId);
          const isRemovedFlyer =
            !isExpiredFlyer && removedFlyers.some(flyer => flyer.flyerId === flyerId);

          if (isActiveFlyer) removedActiveCount += 1;
          if (isExpiredFlyer) removedExpiredCount += 1;
          if (isRemovedFlyer) removedRemovedCount += 1;
        });

        if (removedActiveCount > 0) {
          clearSavedFlyersCache(client.cache, FlyerStatus.active);
          setLiveRegionMessage(`${removedActiveCount} flyer(s) removed from saved list.`);
        }
        if (removedExpiredCount > 0) {
          clearSavedFlyersCache(client.cache, FlyerStatus.expired);
          setLiveRegionMessage(`${removedExpiredCount} expired flyer(s) removed.`);
        }
        if (removedRemovedCount > 0) {
          clearSavedFlyersCache(client.cache, FlyerStatus.removed);
          setLiveRegionMessage(`${removedRemovedCount} removed flyer(s) deleted.`);
        }

        setTimeout(() => {
          if (nextFocusRef.current) {
            nextFocusRef.current.focus();
          } else {
            handleFocusAfterAction();
          }
        }, 500);
      }
    } catch (err) {
      console.error(err);
      showToast({ message: DEFAULT_ERROR_MESSAGE });
    }
  };

  const onRemoveSavedFlyer = async () => {
    const flyerIds = [unsaveFlyerId];
    await onUnSaveFlyers(flyerIds);
    onCancelRemoveSavedFlyer();
  };

  const handleFocusAfterAction = () => {
    const goJarButton = document.querySelector<HTMLElement>('#go-jar-button');
    if (goJarButton) {
      goJarButton.focus();
    } else {
      const firstFlyer = document.querySelector<HTMLElement>(
        '[data-testid="SavedFlyerCard"] [role="link"]'
      );
      if (firstFlyer) {
        firstFlyer.focus();
      } else {
        anchorRef.current?.focus();
      }
    }
  };

  const findNextFocusableElement = (
    currentElement: HTMLElement,
    container: HTMLElement
  ): HTMLElement | null => {
    const focusableSelectors = ['button:not([disabled])', '[tabindex]:not([tabindex="-1"])'];

    const focusableElements = Array.from(
      container.querySelectorAll<HTMLElement>(focusableSelectors.join(','))
    );
    focusableElements.sort((a, b) => {
      // eslint-disable-next-line no-bitwise -- compareDocumentPosition returns a bitmask; this is the standard way to read it
      return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    });

    const currentIndex = focusableElements.indexOf(currentElement);

    if (currentIndex >= 0 && currentIndex < focusableElements.length - 1) {
      return focusableElements[currentIndex + 1];
    }
    if (currentIndex >= 3 && currentIndex <= focusableElements.length - 1) {
      return focusableElements[currentIndex - 3];
    }

    return null;
  };

  const onOpenRemovalModal = (flyerId: number) => {
    setUnsaveFlyerId(flyerId);
    const currentElement = document.activeElement as HTMLElement;
    const mainElement = document.querySelector<HTMLElement>('main');
    if (currentElement && mainElement) {
      const nextElement = findNextFocusableElement(currentElement, mainElement);
      nextFocusRef.current = nextElement;
    }
  };

  const renderGoExploreLink = () => (
    <ButtonFlatMediumEmphasized
      onClick={() => navigate(APP_ROUTES.jar)}
      id="go-jar-button"
      autoFocus
    >
      Go To Jar
    </ButtonFlatMediumEmphasized>
  );

  const clearAllButton = ({ isExpired, label }: { isExpired: boolean; label: string }) => (
    <ButtonFlatMediumEmphasized
      onClick={() => onClickUnsaveFlyers({ isExpired })}
      aria-label={`Clear All ${label}`}
    >
      Clear All
    </ButtonFlatMediumEmphasized>
  );

  const renderInfiniteScroll = ({
    flyers,
    total,
    nextPage,
    isExpired = false,
    isRemoved = false,
  }: {
    flyers: FlyerCardThumbnail[];
    total: number;
    nextPage: () => void;
    isExpired?: boolean;
    isRemoved?: boolean;
  }) => {
    const flyersTotal = flyers.length;
    const hasMore = flyersTotal < total;

    return (
      <InfiniteScroll
        style={{ overflow: 'hidden' }}
        dataLength={flyersTotal}
        next={nextPage}
        hasMore={hasMore}
        loader={<LoadingSpinner />}
      >
        <div role="list">
          {flyers.map(flyer => (
            <div key={flyer.flyerId} role="listitem">
              <Card
                flyer={flyer}
                trackId={trackId}
                isExpired={isExpired}
                isRemoved={isRemoved}
                unsaveFlyer={onOpenRemovalModal}
                flyerType={flyer.type}
                isExpandedCommunity={flyer.isExpandedCommunity}
                titleTranslations={flyer.titleTranslations}
                isCommunityFree={flyer.isCommunityFree}
              />
            </div>
          ))}
        </div>
      </InfiniteScroll>
    );
  };

  const currentProgramList = useMemo(
    () =>
      activeFlyers.length > 0 &&
      renderInfiniteScroll({
        flyers: activeFlyers,
        total: totalActiveFlyers,
        nextPage: fetchMoreActiveFlyers,
      }),
    [activeFlyers, totalActiveFlyers]
  );

  const expiredFlyersList = useMemo(
    () =>
      expiredFlyers.length > 0 && (
        <>
          <Divider className={styles.dividerSpacing} />
          <section className={styles.section}>
            <div className={styles.rowContainer}>
              <TitleSmallEmphasized as="h2">Expired Flyers</TitleSmallEmphasized>
              {clearAllButton({ isExpired: true, label: 'Expired Flyers' })}
            </div>
          </section>
          {renderInfiniteScroll({
            flyers: expiredFlyers,
            total: totalExpiredFlyers,
            nextPage: fetchMoreExpiredFlyers,
            isExpired: true,
          })}
        </>
      ),
    [expiredFlyers]
  );

  const removedFlyersList = useMemo(
    () =>
      removedFlyers.length > 0 && (
        <>
          <Divider className={styles.dividerSpacing} />
          <section className={styles.section}>
            <div className={styles.rowContainer}>
              <TitleSmallEmphasized as="h2">Removed Flyers</TitleSmallEmphasized>
              {clearAllButton({ isExpired: false, label: 'Removed Flyers' })}
            </div>
          </section>
          {renderInfiniteScroll({
            flyers: removedFlyers,
            total: totalRemovedFlyers,
            nextPage: fetchMoreRemovedFlyers,
            isRemoved: true,
          })}
        </>
      ),
    [removedFlyers]
  );

  const handleSortChange = (value: string) => {
    setSelectedSortValue(value);
    setSearchParams({ sort: value }, { replace: true });
    trackEvent({
      event: TrackingEvents.sortSavedFlyers,
      event_detail: TrackingEvents.sortSavedFlyers,
      stage: TrackingStage.consideration,
      event_from: TrackingEvents.viewSavedFlyers,
      event_detail_from: TrackingEvents.viewSavedFlyers,
      stage_from: TrackingStage.consideration,
    });

    handleCloseDrawer();
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    anchorRef.current?.focus();
  };

  return (
    <div ref={ref}>
      <LiveFeedback aria-live="polite" aria-atomic="true" message={liveRegionMessage} />
      <RemoveSavedFlyerModal
        isOpen={!!unsaveFlyerId}
        onConfirm={onRemoveSavedFlyer}
        onCancel={onCancelRemoveSavedFlyer}
      />
      <NavBarBackButton trackId="savedPage">Saved</NavBarBackButton>
      <main>
        <div className={styles.container}>
          <div className={styles.content}>
            <section className={styles.section}>
              <div className={styles.rowContainer}>
                <TitleSmallEmphasized as="h2">Current Flyers</TitleSmallEmphasized>
                <AccessibleDivButton onClick={() => setIsDrawerOpen(!isDrawerOpen)} ref={anchorRef}>
                  <Tag variant="outline" rightIcon={<SwapVertIcon sx={{ fontSize: '15px' }} />}>
                    Sort
                  </Tag>
                </AccessibleDivButton>
              </div>

              {loading &&
                activeFlyers.length === 0 &&
                expiredFlyers.length === 0 &&
                removedFlyers.length === 0 && <LoadingSpinner />}

              {!loading && activeFlyers.length === 0 && (
                <EmptyState
                  title="You currently do not have any saved flyers"
                  titleColor={colors.slate}
                  description={
                    <span>
                      Don't let the good flyers fly away! Search and browse for flyers of interest,
                      then click the star icon (
                      <span className={styles.iconEmptyState}>
                        <FavoriteIcon isFavorite={false} size={24} />
                      </span>
                      ) to save them and easily find then again here in the future.
                    </span>
                  }
                  bottom={renderGoExploreLink()}
                />
              )}
            </section>
            {currentProgramList}
            {expiredFlyersList}
            {removedFlyersList}
          </div>
        </div>
      </main>
      <SortMenu
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        onChange={handleSortChange}
        selectedOption={selectedSortValue}
        anchorRef={anchorRef}
        options={sortOptions}
      />
    </div>
  );
};
